import {defineStore} from "pinia";
import {FundService} from "../services/fund.service";
import * as res from "autoprefixer";

export const useFundStore = defineStore('fund', {
  state: () => ({
    fundService: null,
    organizationId: null,
    modelName: "Fund",
    // these are for analytic events
    traceableType: "Fund",
    traceableName: "fund",
    traceableAssociation: "Donation",

    loading: true,
    funds: [],
    donations: [],
    currentRecord: {},
    loadingRecord: true,
    showNewModal: false,
    meta: {
      page: 1,
      pages: 1
    },
    filter: {
      sort: {
        key: "created_at",
        order: "asc"
      },
      search: {
        key: "name_cont", // Ransack search query, add more here to expand the search results
        term: null
      }
    },

    errors: null
  }),

  actions: {

    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.fundService = new FundService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },
    async loadData() {
      try {
        await this.getFunds();
      } catch (e) {
        console.error("Error loading data", e);
      }
    },
    /**
     * This gets the fund and the associated donations (gifts)
     * @param id
     * @returns {Promise<void>}
     */
    async getFund(id) {
      this.loadingRecord = true;
      this.donations = [];
      // Making this a chain of promises to load async
      return this.fundService.show(id)
        .then((res) => { // Getting the fund record
          this.currentRecord = res.record;
          this.permissions = res.permissions;
          this.loadingRecord = false;
          return this.fundService.donations(id); // This starts the next in the chain
        })
        .then((res) => { // Getting the associated Donations (gifts)
          this.donations = res.data;
        })
        .catch((e) => {
          // growl would go here
          console.error("fgf-1 : error loading fund", e);
        });
    },
    async getFunds() {
      this.loading = true;
      let params = new URLSearchParams();

      return this.fundService.paginate(this.meta.page, params, this.filter)
        .then((res) => {
          this.funds = res.data;
          this.permissions = res.permissions;
          this.meta = res.meta;
        })
        .catch((e) => {
          // growl would go here
          console.error("fgfs-1 : error loading fund", e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async createFund(newFund) {
      const bannerImage = newFund.banner_image;
      let params = {
        ...newFund,
        "organization_id": this.organizationId
      };
      delete params.banner_image;
      const res = await this.fundService.create(params);
      if (!res || res.status >= 400) {
        throw new Error(res?.json?.errors || 'Failed to create fund');
      }
      if (bannerImage && res?.json?.id) {
        await this.updateBannerImage(res.json.id, bannerImage);
      }
      setTimeout(() => {
        this.showNewModal = false;
      }, 1500);
      await this.getFunds();
    },
    async updateBannerImage(id, file) {
      await this.fundService.uploadBannerImage(id, file);
    },
    async updateFund(data) {
      this.loadingRecord = true;
      let params = {
        ...data
      };
      let res = await this.fundService.update(this.currentRecord.id, params);
      if (data.banner_image) {
        await this.updateBannerImage(data.id, data.banner_image);
      }
      await this.getFunds();
      await this.getFund(this.currentRecord.id);
      return res;
    },
    /**
     * Method used in generic Dashboard Show component to load the record.
     * @param id
     */
    async getRecord(id) {
      await this.getFund(id);
    },
    /**
     * Method used in generic Dashboard Show component to update the record.
     * @param data
     */
    async updateRecord(data) {
      await this.updateFund(data);
    },
    nextPage() {
      this.meta.page++;
      this.getFunds();
    },
    prevPage() {
      this.meta.page--;
      this.getFunds();
    },
    moveToPage(page) {
      this.meta.page = page;
      this.getFunds();
    },
    /**
     * Validate the passed edited state.
     * @param local
     */
    async validate(local) {
      this.errors = {};
      if (!local.name) {
        this.errors['name'] = "Name required";
      }
    },
    async removeBannerImage() {
      try {
        const response = await this.fundService.removeBannerImage(this.currentRecord.id);
        if (response && response.status === 200) {
          this.currentRecord = {...this.currentRecord, banner_image_url: null};
        } else {
          console.error("Unexpected response when removing fund image:", response);
        }
      } catch (error) {
        console.error("Error removing fund image:", error);
      }
    },


    // Sets the Ransack sort terms based on the passed key
    // This gets passed to index method to sort the paginated results
    sort(key) {
      if (this.filter.sort.key === key) {
        // If the passed key is the current key, flip the current sort order
        this.filter.sort.order = this.filter.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        // Otherwise set the new key and order by Ascending
        this.filter.sort.key = key;
        this.filter.sort.order = 'asc';
      }

      this.getFunds();
    },

    search(term) {
      this.filter.search.term = term;
      this.meta.page = 1; //this makes it paginate from page 1 not just current page (resets pagination)
      this.getFunds();
    },
  },

  getters: {
    getCurrentMemberDonations(state) {
      if (state.donations) {
        return state.donations;
      } else {
        return [];
      }
    },
    /**
     * Method used in generic Dashboard Index component.
     * Returns an object containing the index table data.
     * @param state
     */
    getIndexTableData(state) {
      const labels = [
        {label: 'Fund Name', key: 'name', sort: true, search: true },
        {label: 'Gifts', key: 'total_contributions', sort: true },
        {label: 'Raised', key: 'all_time_contribution_total', format: 'money', sort: true },
        {label: '', key: 'tags', format: 'tags', sort: false, search: true },
        {label: 'Created Date', key: 'created_at', format: 'date', sort: true },
      ];
      // Splice in the org field if the current org has any children.
      // So it only shows for multi level orgs
      if(state.organization?.child_ids?.length) {
        labels.splice(1, 0, {label: "Organization", key: 'organization_name', sort: true, ransackKey: "organization_name"});
      }
      if (state.funds.length > 0) {
        let funds = state.funds.map((fund) => {
          fund.tags = [];
          if (fund.org_default) {
            fund.tags.push({style: 'primary', value: 'Default Fund'});
          }

          if (fund.archived === true) {
            fund.tags.push({ style: 'tertiary', value: 'Inactive' });
          } else if (fund.visible === false) {
            fund.tags.push({ style: 'hidden', value: 'Hidden' });
          } else {
            fund.tags.push("Active");
          }

          return fund;
        });
        return {
          labels: labels,
          rows: funds
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object containing the record table data.
     * @param state
     */
    getShowTableData(state) {
      let labels = [
        'label',
        'data',
      ];
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = [
          {
            label: 'Name',
            data: state.currentRecord.name
          },
          {
            label: 'Short Description',
            data: state.currentRecord.short_description
          },
          {
            label: 'Long Description',
            data: state.currentRecord.description
          },
          {
            label: 'Created Date',
            data: {
              format: 'date',
              value: state.currentRecord.created_at
            }
          },
          {
            label: 'Total Gifts',
            data: state.currentRecord.total_contributions,
            tooltip: 'Updates every few minutes'
          },
          {
            label: 'Total Amount Raised',
            data: {
              format: 'money',
              value: state.currentRecord.all_time_contribution_total
            },
            tooltip: 'Updates every few minutes'
          },
          {
            label: 'Latest Gift',
            data: {
              format: 'date',
              value: (state.currentRecord.last_donation) ? state.currentRecord.last_donation.created_at : ''
            }
          },
          {
            label: 'Latest Gift Amount',
            data: {
              format: 'money',
              value: (state.currentRecord.last_donation) ? state.currentRecord.last_donation.amount : ''
            }
          },
          {
            label: "Donation Suggestions",
            data: {
              format: 'boolean',
              value: state.currentRecord.donation_suggestions_enabled,
              trueValue: "Enabled",
              falseValue: "Disabled"
            }
          },
          {
            label: "Suggested Donations",
            data: {
              format: 'array',
              value: state.currentRecord.donation_suggestions,
              display: function (val) {
                return "$" + val.toString();
              } // Display Formatting Closure
            }
          },
          {
            label: "Donor Wall",
            data: {
              format: 'boolean',
              value: state.currentRecord.donor_wall_enabled,
              trueValue: "Enabled",
              falseValue: "Disabled"
            }
          },
          {
            label: 'Banner Image',
            data: {
              format: 'image',
              value: state.currentRecord.banner_image_url
            }
          },
          {
            label: "Visibility",
            data: {
              format: 'boolean',
              value: state.currentRecord.visible,
              trueValue: "Visible",
              falseValue: "Hidden"
            }
          },
        ];
        return {
          labels: labels,
          rows: rows
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object with each key being the dashboard panel name of an association,
     *  and each value being the table data for that association.
     */
    getAssociationTableData(state) {
      let labels = [
        {label: 'Name', key: 'name'},
        {label: 'Email', key: 'email'},
        {label: 'Amount', key: 'amount', format: 'money'},
        {label: 'Designation', key: 'target_name'},
        {label: 'Status', key: 'status'},
        {label: 'Date of Gift', key: 'paid_at', format: 'date'}
      ];

      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = state.getCurrentMemberDonations.map((donation) => {
          donation.name = `${donation.user.first_name} ${donation.user.last_name}`;
          donation.email = donation.user.email;
          donation.status = String(donation.status).charAt(0).toUpperCase() + String(donation.status).slice(1);
          return donation;
        });
        return {
          gifts: {
            labels: labels,
            rows: rows
          }
        };
      } else {
        return {
          gifts: {
            labels: labels,
            rows: []
          }
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns the data needed for the editing form table.
     * @param state
     */
    getEditingTableData(state) {
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        const rows = [
          {label: 'Name', key: 'name', input: 'text'},
          {label: 'Short Description', key: 'short_description', input: 'text'},
          {label: 'Long Description', key: 'description', input: 'textarea'},
          {label: 'Donation Suggestions Enabled', key: 'donation_suggestions_enabled', input: 'checkbox'},
          {
            label: 'Donation Suggestions',
            helperText: "Maximum of 4 donation suggestions recommended.",
            key: 'donation_suggestions',
            input: 'chips',
            inputType: 'number',
            displayValue: function (val) {
              return "$" + val.toString();
            } // Display Formatting Closure
          },
          {label: 'Donor Wall Enabled', key: 'donor_wall_enabled', input: 'checkbox'},
          {label: 'Banner Image', key: 'banner_image', input: 'image', removeButton: true}
        ];

        if (!state.currentRecord.org_default) {
          rows.push({
            label: 'Visible',
            key: 'visible',
            input: 'checkbox'
          });
        }

        return rows;
      } else {
        return [];
      }
    }
  }
});
